import { useCallback, useMemo, useRef, useState } from "react";

import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";

import { toggleMediaEntity } from "@/components/alerts/add-v2/model/mediaLinks";
import { reorderMedia as reorder } from "@/components/alerts/add-v2/model/mediaOrder";
import { BulletinMediaItem } from "@/components/alerts/add-v2/model/types";
import { useBulletinRow } from "@/components/alerts/add-v2/persistence/bulletinRowContext";
import {
  AttachmentPatch,
  createBulletinMediaRepository,
  MediaSnapshot,
  mergeMediaSnapshot,
} from "@/components/alerts/add-v2/persistence/mediaRepository";
import { useBulletinField } from "@/components/alerts/add-v2/state/bulletinFormContext";

/**
 * Everything the media section does to the backend, and what it writes back into the form.
 *
 * The form's `media` and `pdf` are a mirror of what the bulletin row already holds. Uploads, deletes
 * and reorders have happened by the time it changes — `busy` is what the UI shows meanwhile.
 * **Entity links are not here at all any more**: they ride the document (decision 46), so attaching a
 * file to a person is a form write and the autosave carries it.
 *
 * ## One media write at a time
 *
 * Every one of these endpoints reads the bulletin, changes one thing and answers with the **whole
 * row**. Two of them in flight together is therefore not just a lost update, it is a lost *list*: the
 * older response describes a bulletin without the newer change and puts it back. `addMedia` always
 * knew this about itself — it uploads sequentially rather than with `Promise.all` — and a burst of
 * reorder drops needs the same guarantee.
 *
 * So there is **one queue** (`chain`), and every call here joins it in the order the officer acted.
 */

export interface MediaUploads {
  /** True while an upload or a delete is in flight. The drop zones disable themselves on it. */
  busy: boolean;
  addMedia: (files: File[]) => Promise<void>;
  removeMedia: (item: BulletinMediaItem) => Promise<void>;
  /**
   * Swaps a file for an edited version of itself, under a new name (`editedFilename`).
   *
   * Three calls, in this order and no other: **upload, delete, then put it back where it was.** A
   * failed delete leaves two copies, which is one click to fix; a delete that went first and was
   * followed by a failed upload has lost the photo. The replacement is a different file as far as
   * every endpoint is concerned, so what the *document* owns has to be carried over by hand —
   * `entityIds` and `description` are keyed by filename (decision 22) and the new name has neither.
   */
  replaceMedia: (item: BulletinMediaItem, file: File) => Promise<void>;
  /**
   * Attaches or detaches one entity on one file — the write behind both halves of decision 8's
   * relation, whichever side the officer used.
   *
   * A plain form write, with no request of its own: the relation rides the document (decision 46),
   * so the autosave that carries the entities carries their links in the same transaction. That is
   * not a convenience — the link is a foreign key onto a row the document is what creates.
   */
  toggleEntity: (mediaId: string, entityId: string) => void;
  /**
   * Moves `activeId` to where `overId` sits. Optimistic and queued like `toggleEntity`, and for
   * the same reason: a drop has to land under the finger that made it, not after a round trip.
   */
  reorderMedia: (activeId: string, overId: string) => void;
  setPdf: (file: File) => Promise<void>;
  removePdf: () => Promise<void>;
}

export const useMediaUploads = (): MediaUploads => {
  const { t } = useTranslation("bulletin/create-v2");
  const row = useBulletinRow();
  const repository = useMemo(() => createBulletinMediaRepository(row), [row]);

  const [busy, setBusy] = useState(false);
  const [, setMedia] = useBulletinField("media");
  const [, setPdf] = useBulletinField("pdf");

  /**
   * The tail of the write queue. Every task put on it swallows its own failures, so the chain never
   * rejects and one failed call cannot strand the ones behind it.
   */
  const chain = useRef<Promise<void>>(Promise.resolve());
  /** `filename` → the links to store for it. Latest write wins, so a burst collapses. */
  const pendingWrites = useRef(new Map<string, AttachmentPatch>());
  /** The list as it was before the current burst of toggles, to roll back to if one fails. */
  const writeBaseline = useRef<BulletinMediaItem[] | null>(null);

  const enqueue = useCallback((task: () => Promise<void>): Promise<void> => {
    chain.current = chain.current.then(task);
    return chain.current;
  }, []);

  const apply = useCallback(
    (snapshot: MediaSnapshot) => {
      setMedia((previous) => mergeMediaSnapshot(previous, snapshot.media));
      setPdf(snapshot.pdf);
    },
    [setMedia, setPdf],
  );

  /** One `busy` window and one error message per user action, whatever it takes to carry it out. */
  const run = useCallback(
    (action: () => Promise<void>, message: string) =>
      enqueue(async () => {
        setBusy(true);
        try {
          await action();
        } catch (error) {
          console.error(message, error);
          toast.error(message);
        } finally {
          setBusy(false);
        }
      }),
    [enqueue],
  );

  const addMedia = useCallback(
    (files: File[]) =>
      run(async () => {
        // Sequential for the reason at the top of this file, and it is also what gives the files
        // their upload order as `sort_ordinal`.
        for (const file of files) {
          apply(await repository.uploadMedia(file));
        }
      }, t("media.error_upload")),
    [run, apply, repository, t],
  );

  const removeMedia = useCallback(
    (item: BulletinMediaItem) =>
      run(async () => {
        apply(await repository.deleteMedia(item.filename));
      }, t("media.error_delete")),
    [run, apply, repository, t],
  );

  const replaceMedia = useCallback(
    (item: BulletinMediaItem, file: File) =>
      run(async () => {
        const uploaded = await repository.uploadMedia(file);
        apply(uploaded);

        // **Not `file.name`.** The upload renames a filename the bulletin already has, so what
        // was stored can differ from what was sent — and both writes below key on the stored
        // name. Keyed on `file.name`, the fixup matched no row and `patchAttachment` asked for a
        // filename the bulletin does not contain, so the replacement kept the ordinal, links and
        // caption of a newly uploaded file instead of the one it replaced.
        //
        // The upload assigns `max_sort_ordinal + 1` and the snapshot comes back in that order, so
        // the replacement is the last row.
        const stored =
          uploaded.media[uploaded.media.length - 1]?.filename ?? file.name;

        apply(await repository.deleteMedia(item.filename));

        // Put the replacement back in the slot the original held, and carry across the two fields
        // the file itself does not own.
        setMedia((previous) =>
          previous.map((row) =>
            row.filename === stored
              ? {
                  ...row,
                  sortOrdinal: item.sortOrdinal,
                  entityIds: item.entityIds,
                  description: item.description,
                }
              : row,
          ),
        );
        apply(
          await repository.patchAttachment(stored, {
            sortOrdinal: item.sortOrdinal,
          }),
        );
      }, t("media.error_edit")),
    [run, apply, repository, setMedia, t],
  );

  const setPdfFile = useCallback(
    (file: File) =>
      run(async () => {
        apply(await repository.uploadPdf(file));
      }, t("media.error_upload")),
    [run, apply, repository, t],
  );

  const removePdf = useCallback(
    () =>
      run(async () => {
        apply(await repository.deletePdf());
      }, t("media.error_delete")),
    [run, apply, repository, t],
  );

  /** Sends one file's outstanding fields, if it still has any by the time its turn comes. */
  const flushWrites = useCallback(async () => {
    const outstanding = pendingWrites.current.entries().next();
    if (outstanding.done) return;

    const [filename, patch] = outstanding.value;
    pendingWrites.current.delete(filename);

    try {
      const snapshot = await repository.patchAttachment(filename, patch);

      // Only the write that finds nothing behind it may put the server's list back. An earlier
      // response is already stale against the edits queued after it — it would undo them on
      // screen, and the next flush would then send the undone state as if the officer had meant it.
      if (pendingWrites.current.size === 0) {
        writeBaseline.current = null;
        apply(snapshot);
      }
    } catch (error) {
      console.error("Failed to store a change to a file", error);
      toast.error(t("media.error_order"));

      // The whole burst rolls back, not just this file: the queue stops at the first failure, so
      // nothing after it was stored either, and reverting to where the burst started is the only
      // state the officer can be told is true.
      pendingWrites.current.clear();
      const baseline = writeBaseline.current;
      writeBaseline.current = null;
      if (baseline) setMedia(baseline);
    }
  }, [repository, apply, setMedia, t]);

  /** Queues one file's `patch`, merged onto anything already outstanding for it. */
  const queueWrite = useCallback(
    (filename: string, patch: AttachmentPatch) => {
      pendingWrites.current.set(filename, {
        ...pendingWrites.current.get(filename),
        ...patch,
      });
      enqueue(flushWrites);
    },
    [enqueue, flushWrites],
  );

  const toggleEntity = useCallback(
    (mediaId: string, entityId: string) => {
      // The updater form, not this render's value: `useBulletinField` resolves it against
      // `getValues`, so two clicks in the same tick compose instead of the second overwriting the
      // first. The burst of requests that used to need a queue is gone with the request itself —
      // several clicks are now several writes to one document, which autosave sends once.
      setMedia((previous) => toggleMediaEntity(previous, mediaId, entityId));
    },
    [setMedia],
  );

  const reorderMedia = useCallback(
    (activeId: string, overId: string) => {
      let moved: { filename: string; sortOrdinal: number } | undefined;

      setMedia((previous) => {
        const reordered = reorder(previous, activeId, overId);
        if (!reordered) return previous;
        if (writeBaseline.current === null) writeBaseline.current = previous;
        moved = reordered.moved;
        return reordered.items;
      });

      // One file's ordinal, not the list: the server holds the order per file, so a drop tells it
      // about the file that moved and every other file keeps the ordinal it already had.
      if (moved) queueWrite(moved.filename, { sortOrdinal: moved.sortOrdinal });
    },
    [setMedia, queueWrite],
  );

  return {
    busy,
    addMedia,
    removeMedia,
    replaceMedia,
    toggleEntity,
    reorderMedia,
    setPdf: setPdfFile,
    removePdf,
  };
};
